import { useState } from 'react';

import { Button } from '@/shared/ui/shadcn/button';
import { Label } from '@/shared/ui/shadcn/label';

import { DS_STEP_FORM_ID } from './_shared/dsStepFormId';
import { ExperianMobileWaitView } from './_shared/ExperianMobileWaitView';
import { OnboardingStepFrame } from './_shared/OnboardingStepFrame';

export interface IdpQuestionsStepProps {
  questions: readonly string[];
  answerChoices: readonly string[][];
  onSubmit: (answers: string[]) => void;
  onRefresh: () => void;
  isSubmitting: boolean;
  submitError: Error | null;
}

/**
 * KBA-question step.
 *
 * Per DoseSpot's V2 IDP workflow, each submitted answer must be the 1-based
 * numerical index of the selected choice within that question's options
 * array (e.g. submit `"5"` for the fifth option). The order of submitted
 * answers must positionally match the order of `questions` returned by
 * `POST /idp`.
 *
 * We therefore key local selection state by question *index* (not by
 * question text) and remember the selected *choice index* (not the choice
 * text). This:
 *   - Avoids a silent state-collision bug if Experian ever returned two
 *     near-identical question strings: keying by text would let the second
 *     question overwrite the first user's selection.
 *   - Lets us emit a deterministic, positional, 1-based numeric string array
 *     on submit -- which is what DoseSpot's API actually expects.
 */
export function IdpQuestionsStep(props: IdpQuestionsStepProps) {
  const { questions, answerChoices, onSubmit, onRefresh, isSubmitting, submitError } = props;
  const [selectedChoiceByQuestion, setSelectedChoiceByQuestion] = useState<Map<number, number>>(
    new Map()
  );

  // Reset selections when the upstream KBA challenge changes.
  //
  // While the user sits on `answer_questions`, useCurrentStepQuery refetches
  // every 5s and can hand us a *different* set of questions without
  // unmounting this component.

  // Render-time reset via the "compare prev prop" pattern (rather than an
  // effect) keeps us off react-hooks/set-state-in-effect and avoids the
  // wasted commit a useEffect would impose.
  const challengeFingerprint = JSON.stringify({ q: questions, c: answerChoices });
  const [previousFingerprint, setPreviousFingerprint] = useState(challengeFingerprint);
  if (challengeFingerprint !== previousFingerprint) {
    setPreviousFingerprint(challengeFingerprint);
    setSelectedChoiceByQuestion(new Map());
  }

  // Build the positional answer vector once, dropping unanswered questions.
  const orderedChoiceIndices = questions
    .map((_, qIndex) => selectedChoiceByQuestion.get(qIndex))
    .filter((cIndex): cIndex is number => cIndex !== undefined);
  const allAnswered = orderedChoiceIndices.length === questions.length;

  const handleSelectAnswer = (qIndex: number, cIndex: number) => {
    setSelectedChoiceByQuestion((prev) => new Map(prev).set(qIndex, cIndex));
  };

  const handleSubmit = () => {
    if (!allAnswered || isSubmitting) {
      return;
    }
    onSubmit(orderedChoiceIndices.map((cIndex) => (cIndex + 1).toString()));
  };

  if (questions.length === 0) {
    return <ExperianMobileWaitView onRefresh={onRefresh} />;
  }

  return (
    <form
      id={DS_STEP_FORM_ID}
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <OnboardingStepFrame
        title="Answer verification questions"
        description={
          submitError
            ? undefined
            : 'Please answer the following questions to verify your identity. Select one answer for each question.'
        }
        error={
          submitError
            ? {
                title: 'Could not submit answers',
                description: submitError.message,
                action: (
                  <Button type="button" variant="secondary" onClick={handleSubmit}>
                    Retry
                  </Button>
                ),
              }
            : undefined
        }
      >
        <div className="flex flex-col gap-6">
          {questions.map((question, qIndex) => {
            const choices = answerChoices[qIndex] ?? [];
            const selectedCIndex = selectedChoiceByQuestion.get(qIndex);

            return (
              <fieldset key={qIndex} className="space-y-3">
                <legend className="text-sm font-medium">
                  {qIndex + 1}. {question}
                </legend>
                <div className="flex flex-col gap-2 pl-4">
                  {choices.map((choice, cIndex) => {
                    const inputId = `q${qIndex}-c${cIndex}`;
                    const isSelected = selectedCIndex === cIndex;

                    return (
                      <label
                        key={cIndex}
                        htmlFor={inputId}
                        className={`flex cursor-pointer items-center gap-3 rounded-md border p-3 text-sm transition-colors ${
                          isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:bg-muted/50'
                        }`}
                      >
                        <input
                          type="radio"
                          id={inputId}
                          name={`question-${qIndex}`}
                          value={(cIndex + 1).toString()}
                          checked={isSelected}
                          onChange={() => handleSelectAnswer(qIndex, cIndex)}
                          className="accent-primary h-4 w-4"
                        />
                        <Label htmlFor={inputId} className="cursor-pointer font-normal">
                          {choice}
                        </Label>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            );
          })}
        </div>
      </OnboardingStepFrame>
    </form>
  );
}
